/**
 * Primary navigation model - the single source of truth for the app's primary
 * surfaces. Consumed via `useNavContext()` (lib/use-nav-context.ts) and rendered
 * by `NavList` in BOTH the desktop rail (AppSidebar, lg+) and the mobile drawer
 * (MobileNavDrawer, below lg), so visibility/gating lives in exactly one place.
 *
 * Visibility is permission-gated on the user's role and whether they have an
 * active (DPA-accepted) practice. The server independently enforces access on
 * every route - this only governs what we surface in the chrome.
 *
 * Only top-level routes that actually exist in App.tsx are listed here. Billing
 * and Documents are case-scoped (no global index route) and are reached from a
 * case, so they are intentionally not primary-nav items (no dead links).
 */
import {
  ClipboardList, FolderKanban, Cog,
  HeartHandshake, Library, LifeBuoy,
  BookOpen, UserCircle, Building2, ScrollText, BarChart3,
  LayoutDashboard, ShieldCheck, Clock, CreditCard, Files, FileText, Users,
  Gauge,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { listCases } from '../api/cases.ts';
import { useTokenStore } from '../api/use-api.ts';
import { clientViewDoorVisible } from './client-view-door.ts';
import { usePersona } from './use-persona.ts';
import { practiceSurfaceAccess, type PracticeAccessInput } from './practice-access.ts';
import { useActivePractice } from './active-practice.tsx';
import { type PrimaryNavItem, type PrimaryNavSubItem, useCaseNavItems } from './case-nav-items.ts';

// Re-exported for existing callers — the types and `useCaseNavItems` itself live in
// case-nav-items.ts so a test can import the real function without pulling in JSX
// (tsconfig.test.json carries no --jsx; case-nav-capability-completeness.test.ts).
export type { PrimaryNavItem, PrimaryNavSubItem };
export { useCaseNavItems };

/**
 * The visible primary-nav items for the current user, already filtered by role
 * and active-practice state. Returns Dashboard always; clinical surfaces only
 * once the user has a DPA-accepted membership.
 *
 * Persona for nav gating is derived from the user's RELATIONSHIPS (practice
 * memberships), not the global account role (ADR 0001 Addendum A / F7). A person
 * invited as staff keeps a 'practice_owner' account stamp (registration hardcodes
 * it), so the account role is not a reliable signal for what to surface - e.g. a
 * tutor would otherwise wrongly see owner/clinician items. The one legitimately
 * global distinction, platform scope, is still read from the account role.
 * The server independently enforces access on every route; this only governs chrome.
 */
export function usePrimaryNavItems(): PrimaryNavItem[] {
  // Cross-practice predicates come from usePersona, so every entry point agrees.
  const { isPlatformAdmin, isClientOnly, hasActivePractice } = usePersona();
  // Compliance and the Library both left this rail: each acted on a practice while
  // presenting as global.
  const { memberships } = useActivePractice();
  // The CASE axis, not memberships: a parent employed at her child's practice is
  // support_worker on the practice axis and client_contact only on the case, and she is
  // exactly who this door is for. On ERROR it SHOWS — a failed load must never read as
  // "no such relationship".
  const store = useTokenStore();
  const clientCases = useQuery({
    queryKey: ['cases'],
    queryFn: () => listCases(store),
    staleTime: 15_000,
  });
  // not-authorization: persona shell — which door to offer; /portal re-checks server-side.
  const hasClientView = clientViewDoorVisible({
    isError: clientCases.isError,
    cases: clientCases.data?.cases,
  });

  // Help is available to every authenticated user (including parents/clients).
  const helpItem: PrimaryNavItem = {
    to: '/help',
    label: 'Help',
    Icon: LifeBuoy,
    testId: 'nav-help',
    children: [
      { to: '/help?tab=guides', label: 'Guides', testId: 'nav-help-guides' },
      { to: '/help?tab=report', label: 'Report an issue', testId: 'nav-help-report' },
      { to: '/help?tab=feature', label: 'Request a feature', testId: 'nav-help-feature' },
      { to: '/help?tab=feedback', label: 'Give feedback', testId: 'nav-help-feedback' },
    ],
  };

  // A client-only account gets their cases — the same surface everyone else reaches, which
  // adapts to what they hold. A separate "My portal" address for the same view was the
  // structural defect, not a smaller nav.
  if (isClientOnly) {
    return [
      { to: '/cases', label: 'My cases', Icon: HeartHandshake, testId: 'nav-portal', end: true },
      helpItem,
    ];
  }

  // Platform operators (NovaLattix ops) run the SaaS, not a practice. They get
  // an explicit ops-only allowlist - never tenant surfaces (cases / enquiries /
  // library / practice compliance). This is a separate literal list, so tenant
  // leakage is structurally impossible, not filter-dependent.
  if (isPlatformAdmin) {
    return [
      { to: '/', label: 'Operations', Icon: LayoutDashboard, testId: 'nav-dashboard', end: true },
      { to: '/admin/users', label: 'Users', Icon: UserCircle, testId: 'nav-admin-users' },
      { to: '/admin/plans', label: 'Plans', Icon: CreditCard, testId: 'nav-admin-plans' },
      { to: '/admin/practices', label: 'Practices', Icon: Building2, testId: 'nav-admin-practices' },
      { to: '/admin/audit', label: 'Audit log', Icon: ScrollText, testId: 'nav-admin-audit' },
      { to: '/admin/usage', label: 'Usage', Icon: BarChart3, testId: 'nav-admin-usage' },
      { to: '/admin', label: 'Platform admin', Icon: Cog, testId: 'nav-admin' },
      helpItem,
    ];
  }

  /*
   * ZONES: the rail used to run three questions together as one flat list. "Your practices"
   * is also the missing DOOR — practice-scoped screens sit behind /practices/:practiceId,
   * and the only way in had been a dashboard card.
   */
  const all: Array<PrimaryNavItem & { show: boolean }> = [
    { to: '/', label: 'Dashboard', Icon: LayoutDashboard, testId: 'nav-dashboard', end: true, show: true },

    // The two /cases doors carry a LENS: both landing on the identical combined list read
    // as the other door being broken. Each shows its own band; a cross-link offers the other.
    {
      to: '/cases?lens=work',
      label: 'My caseload',
      Icon: FolderKanban,
      testId: 'nav-cases',
      section: 'Your work',
      show: hasActivePractice,
    },
    // Worklog & billing (`/timesheet`) is withdrawn for beta (owner, 2026-08-19): tutor
    // self-billing is a post-beta tier (F25), so its review screen can only be empty.

    // A rail holding only "Practice overview" opens a page with no cards: a door that
    // cannot fill. Asked of the rail itself, so no second definition can drift from it.
    ...memberships
      .filter((m) => practiceNavItems(m.practiceId, m).length > 1)
      .map((m) => ({
      to: `/practices/${m.practiceId}`,
      label: m.practiceName,
      Icon: Building2,
      testId: `nav-practice-${m.practiceId}`,
      // Named, never numbered: "Practice 2" tells a two-practice owner nothing, and telling
      // them apart is the entire reason these screens moved behind a practice.
      section: 'Your practices',
      show: true,
    })),

    {
      to: '/cases?lens=client',
      label: 'Your client view',
      Icon: HeartHandshake,
      testId: 'nav-client-view',
      section: 'Your client view',
      show: hasClientView,
    },

    {
      to: '/profile',
      label: 'My profile',
      Icon: UserCircle,
      // NOT `nav-profile`: the app header already owns that id for its own profile link,
      // and two elements answering to one testid is a strict-mode collision, not a choice.
      testId: 'nav-you-profile',
      // The door to the profile rail, which carries credentials, CPD and agreements. Those
      // are not repeated here: one rail owns them, and it takes over on arrival.
      section: 'You',
      show: true,
    },

    { ...helpItem, show: true },
  ];

  return all.filter((i) => i.show).map(({ show: _show, ...item }) => item);
}

/**
 * Context-sensitive nav items shown when the user is under /practices/:practiceId.
 * Replaces the global nav for that route scope, on both surfaces.
 *
 * Takes the SERVER's resolved capability set for this membership, not the role. Each
 * predicate here used to re-derive a server rule from `role_in_practice` — a copy free
 * to drift from the rule it copied, which is how a tutor came to be offered a screen
 * the server denies (2026-07-22). Now the nav shows an item exactly when the server
 * would allow it.
 */
export function usePracticeNavItems(
  practiceId: string,
  membership: PracticeAccessInput | null,
): PrimaryNavItem[] {
  return practiceNavItems(practiceId, membership);
}

/** The same answer, callable per membership: a `use`-named call in a callback is a
 *  rules-of-hooks error, and the rail asks this once per practice row. */
export function practiceNavItems(
  practiceId: string,
  membership: PracticeAccessInput | null,
): PrimaryNavItem[] {
  if (!practiceId) return [];
  const base = `/practices/${practiceId}`;
  // Each nav item is shown when ANY section of its screen is reachable — the screens
  // are composites, and a single capability would either hide a usable screen or offer
  // an empty one. The previous predicates were `owner || admin` and `owner ||
  // practitioner`. The second was simply wrong: a practitioner holds NEITHER capability
  // the Templates screen needs, so the nav offered them a screen on which both queries
  // 403. That is the drift this migration exists to remove, and it was invisible while
  // the nav re-derived its own rules — which is why the predicates now live in one
  // shared module rather than being copied here.
  const { canGovern, canSeeBilling, canSeeTemplates, canSeeTeam, canSeeEnquiries,
    canSeeCompliance, canSeeLibrary, canSeeCases, canSeePlan } =
    practiceSurfaceAccess(membership);

  const items: PrimaryNavItem[] = [
    { to: base, label: 'Practice overview', Icon: LayoutDashboard, testId: 'practice-nav-overview', end: true },
  ];
  // BUSINESS section — the practice as a company. Enquiries moved here from the global nav
  // in slice D: it always acted on the practice, and presenting it as global meant a
  // two-practice owner could not tell which practice's pipeline they were looking at.
  if (canSeeEnquiries) {
    items.push({ to: `${base}/enquiries`, label: 'Enquiries', Icon: ClipboardList, testId: 'practice-nav-enquiries', section: 'Clients' });
  }
  if (canSeeCases) {
    items.push({ to: `${base}/cases`, label: 'Cases', Icon: FolderKanban, testId: 'practice-nav-cases', section: 'Clients' });
  }
  if (canSeeLibrary) {
    items.push({ to: `${base}/target-library`, label: 'Library', Icon: Library, testId: 'practice-nav-library', section: 'Clinical' });
  }
  if (canSeeTeam) {
    items.push({ to: `${base}/team`, label: 'Team', Icon: Users, testId: 'practice-nav-team', section: 'Business' });
  }
  if (canSeeBilling) {
    items.push({ to: `${base}/invoicing`, label: 'Invoicing', Icon: CreditCard, testId: 'practice-nav-billing', section: 'Business' });
  }
  if (canSeeTemplates) {
    items.push({ to: `${base}/templates`, label: 'Templates', Icon: Files, testId: 'practice-nav-templates', section: 'Practice' });
  }
  if (canGovern) {
    items.push({ to: `${base}/settings`, label: 'Settings', Icon: Cog, testId: 'practice-nav-settings', section: 'Practice' });
  }
  if (canSeeCompliance) {
    items.push({ to: `${base}/compliance`, label: 'Compliance', Icon: ShieldCheck, testId: 'practice-nav-compliance', section: 'Practice' });
  }
  // PRACTICE, not Business: Invoicing is what the practice charges its clients, this is what
  // the practice pays HapMetrix. Sitting them together read as one subject (owner, 2026-08-29).
  if (canSeePlan) {
    items.push({ to: `${base}/plan`, label: 'Plan & usage', Icon: Gauge, testId: 'practice-nav-plan', section: 'Practice' });
  }
  return items;
}

/**
 * Context-sensitive nav shown when the user is anywhere under /profile/*.
 * Replaces the global nav for that scope.
 */
export function useProfileNavItems(
  canKeepCredentials: boolean,
  canLogCpd: boolean,
  canHoldWorkingAgreement: boolean,
): PrimaryNavItem[] {
  const items: PrimaryNavItem[] = [
    { to: '/profile', label: 'My profile', Icon: UserCircle, testId: 'profile-nav-overview', end: true },
  ];
  if (canKeepCredentials) {
    items.push({ to: '/profile/credentials', label: 'Credentials', Icon: ShieldCheck, testId: 'profile-nav-credentials', section: 'Professional' });
  }
  if (canLogCpd) {
    items.push({ to: '/profile/cpd', label: 'CPD log', Icon: BookOpen, testId: 'profile-nav-cpd', section: 'Professional' });
  }
  // Gated, where it used to be unconditional: only a support_worker can be ISSUED a working
  // agreement, so for everyone else this door led to a screen that could never hold anything.
  // A brand-new account with no memberships at all was the worst case — the first thing it was
  // offered after "Get started" was a guaranteed-empty page. The predicate is the persona's,
  // shared with the hub tile in ProfileScreen, so the two doors cannot disagree.
  if (canHoldWorkingAgreement) {
    // 'Professional', not 'Account': it exists because a practice engages this person as a
    // tutor — the profile hub groups it under the same heading.
    items.push({ to: '/profile/agreements', label: 'Working agreements', Icon: FileText, testId: 'profile-nav-agreements', section: 'Professional' });
  }
  items.push(
    { to: '/profile/sessions', label: 'Signed-in devices', Icon: Clock, testId: 'profile-nav-sessions', section: 'Account' },
  );
  return items;
}
